import type { IntlShape } from 'react-intl';

import { MentionedEntity } from '~/eds/src/types/MentionedEntity';
import { User } from '~/eds/src/types/User';
import { getUserName } from '~/eds/src/utils/getUserName';
import { Department } from '~/types/Department';
import { PilotId } from '~/types/PilotId';

import { sortByNumericValue } from './array';

export const transformUserToMention = (
  user: User,
  hasAccess: boolean,
  intl: IntlShape,
) => {
  const userMention: MentionedEntity<'user'> = {
    id: String(user.id),
    type: 'user',
    label: getUserName({
      user,
      options: {
        placeholderName: intl.formatMessage({
          id: 'UserMention.deactivatedUser',
          defaultMessage: 'Deactivated User',
          description: 'Placeholder text for deactivated user name',
        }),
      },
      intl,
    }),
    isInvalid: !hasAccess,
    tooltip: hasAccess
      ? user.email
      : intl.formatMessage({
          id: 'UserMention.noAccessToFile',
          defaultMessage: 'No access to file',
          description:
            'Tooltip text when user does not have access to the file',
        }),
    data: {
      user,
    },
  };
  return userMention;
};

export function createDepartmentMention(department: Department) {
  const { id, name, memberCount } = department;
  const departmentMention: MentionedEntity<'department'> = {
    id: String(id),
    isInvalid: false,
    label: name,
    tooltip: '',
    type: 'department',
    data: {
      department: {
        count: memberCount,
        id,
        name,
      },
    },
  };
  return departmentMention;
}

export function getDepartmentMentions(departmentTree: Department[]) {
  const normalizedDepartments = normalizeDepartmentTree(departmentTree);
  return Object.values(normalizedDepartments)
    .map((department) => createDepartmentMention(department))
    .sort(sortByNumericValue('position'));
}

export function normalizeDepartmentTree(departmentTree: Department[]) {
  const normalizedDepartments: Record<PilotId, Department> = {};

  function normalizeDepartmentTreeR(deptTree: Department[]) {
    deptTree.forEach((department: Department) => {
      const { children, id } = department;

      // add current department
      normalizedDepartments[id] = department;

      // recursively normalize and add departments
      normalizeDepartmentTreeR(children);
    });
  }

  normalizeDepartmentTreeR(departmentTree);

  return normalizedDepartments;
}
